/**
 * 8. 경쟁사 순위 TOP 10 카드
 */
import React, { useState } from 'react';
import { Card, Tag, Space, Button, Popover, Spin } from 'antd';
import {
    BarChartOutlined,
    PictureOutlined,
    ReloadOutlined,
    WarningOutlined,
} from '@ant-design/icons';

const CompetitorRankingCard = ({ report, tagsState, onRetryTags, isSample }) => {
    const product = report.product || {};
    const myTags = new Set(
        (product.tags || '')
            .split(',')
            .map(t => t.trim())
            .filter(Boolean)
    );
    const [showThumbs, setShowThumbs] = useState(true);

    const getTagsForCompetitor = c => {
        if (tagsState?.data?.[c.mallUrl]?.tags) return tagsState.data[c.mallUrl].tags;
        return c.tags || [];
    };

    const getVisit = (c, field) => {
        if (tagsState?.data?.[c.mallUrl]) return tagsState.data[c.mallUrl][field] ?? null;
        return c[field] ?? null;
    };

    const tagsLoading = tagsState?.loading;
    const tagsError = tagsState?.error;

    const missingFields = [];
    const comp = report.competitors || [];
    if (comp.some(c => c.wishCount == null)) missingFields.push('찜수');
    if (comp.some(c => c.reviewCount == null)) missingFields.push('리뷰수');
    if (comp.some(c => c.price == null)) missingFields.push('가격');

    return (
        <Card
            title={
                <span
                    style={{
                        fontSize: 14,
                        fontWeight: 700,
                        color: '#0f172a',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 6,
                    }}
                >
                    <BarChartOutlined style={{ color: '#7c3aed' }} /> 경쟁사 순위 TOP 10
                </span>
            }
            bordered={false}
            className="full-chart-card"
            style={{ marginTop: 24 }}
            extra={
                <Space size={8}>
                    {tagsLoading && <Spin size="small" />}
                    {tagsError && (
                        <Button
                            size="small"
                            icon={<ReloadOutlined />}
                            onClick={onRetryTags}
                            style={{ fontSize: 11, color: '#ef4444', borderColor: '#fca5a5' }}
                        >
                            태그/방문 재조회
                        </Button>
                    )}
                    <span style={{ fontSize: 11, color: '#9ca3af' }}>
                        {report.stats?.[1]?.value || '0'}개 상품 중
                    </span>
                </Space>
            }
        >
            {/* MY 상품 요약 */}
            <div
                style={{
                    background: '#f5f3ff',
                    padding: '16px 20px',
                    borderRadius: 12,
                    display: 'flex',
                    gap: 16,
                    alignItems: 'center',
                    marginBottom: 16,
                }}
            >
                {product.image ? (
                    <img
                        src={product.image}
                        alt=""
                        style={{
                            width: 72,
                            height: 72,
                            borderRadius: 8,
                            objectFit: 'cover',
                            flexShrink: 0,
                        }}
                    />
                ) : (
                    <div
                        style={{
                            width: 72,
                            height: 72,
                            background: '#ffffb8',
                            borderRadius: 8,
                            flexShrink: 0,
                        }}
                    ></div>
                )}
                <div style={{ flex: 1, minWidth: 0 }}>
                    <Space size={6} style={{ marginBottom: 4, flexWrap: 'wrap' }}>
                        <span className="my-badge">MY</span>
                        <span style={{ fontSize: 14, fontWeight: 700, lineHeight: 1.4 }}>
                            {product.title}
                        </span>
                    </Space>
                    <div style={{ fontSize: 13, color: '#6366f1', marginBottom: 6 }}>
                        순위 <span style={{ fontWeight: 800 }}>{product.rank}위</span>
                        {' · '}리뷰{' '}
                        <span style={{ fontWeight: 800 }}>
                            {product.reviewCount?.toLocaleString()}
                        </span>
                        {' · '}찜{' '}
                        <span style={{ fontWeight: 800 }}>
                            {product.wishCount?.toLocaleString()}
                        </span>
                    </div>
                    {myTags.size > 0 && (
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                            {[...myTags].map(tag => (
                                <span key={tag} style={{ fontSize: 10, color: '#7c3aed' }}>
                                    #{tag}
                                </span>
                            ))}
                        </div>
                    )}
                </div>
            </div>

            {/* 범례 + 썸네일 토글 */}
            <div
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    marginBottom: 12,
                }}
            >
                <div style={{ display: 'flex', gap: 16, fontSize: 12 }}>
                    <div
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 5,
                            color: '#4f46e5',
                            fontWeight: 600,
                        }}
                    >
                        <div
                            style={{
                                width: 10,
                                height: 10,
                                borderRadius: '50%',
                                background: '#4f46e5',
                            }}
                        ></div>{' '}
                        공동 사용
                    </div>
                    <div
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 5,
                            color: '#ef4444',
                            fontWeight: 600,
                        }}
                    >
                        <div
                            style={{
                                width: 10,
                                height: 10,
                                borderRadius: '50%',
                                background: '#ef4444',
                            }}
                        ></div>{' '}
                        경쟁사만 사용
                    </div>
                </div>
                <Button
                    size="small"
                    icon={<PictureOutlined />}
                    onClick={() => setShowThumbs(v => !v)}
                    style={{
                        fontSize: 11,
                        color: showThumbs ? '#6366f1' : '#9ca3af',
                        borderColor: showThumbs ? '#6366f1' : '#e2e8f0',
                        background: showThumbs ? '#f5f3ff' : '#fff',
                    }}
                >
                    썸네일
                </Button>
            </div>

            {/* 경쟁사 목록 */}
            <div className="competitor-list">
                <div
                    style={{
                        display: 'flex',
                        padding: '8px 0',
                        borderBottom: '1px solid #f1f3f5',
                        fontSize: 12,
                        color: '#9ca3af',
                        fontWeight: 700,
                        alignItems: 'center',
                    }}
                >
                    <div style={{ width: 48, flexShrink: 0 }}>순위</div>
                    {showThumbs && <div style={{ width: 68, flexShrink: 0 }}></div>}
                    <div style={{ flex: 1 }}>상품명</div>
                    <div style={{ width: 88, textAlign: 'right', flexShrink: 0 }}>리뷰</div>
                    <div style={{ width: 88, textAlign: 'right', flexShrink: 0 }}>찜</div>
                    <div style={{ width: 88, textAlign: 'right', flexShrink: 0 }}>
                        <div>오늘 방문</div>
                        <div style={{ fontSize: 10, color: '#c0c0c0', fontWeight: 400 }}>
                            이스토어 전체 기준
                        </div>
                    </div>
                    <div style={{ width: 88, textAlign: 'right', flexShrink: 0 }}>
                        <div>전체 방문</div>
                        <div style={{ fontSize: 10, color: '#c0c0c0', fontWeight: 400 }}>
                            이스토어 전체 기준
                        </div>
                    </div>
                </div>
                {(report.competitors || []).map((c, idx) => {
                    const tags = getTagsForCompetitor(c);
                    const todayVisit = getVisit(c, 'todayVisit');
                    const totalVisit = getVisit(c, 'totalVisit');
                    return (
                        <div
                            key={idx}
                            style={{
                                display: 'flex',
                                padding: '14px 0',
                                borderBottom: '1px solid #f1f3f5',
                                alignItems: 'flex-start',
                            }}
                        >
                            {/* 순위 */}
                            <div
                                style={{
                                    width: 48,
                                    flexShrink: 0,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    paddingTop: 6,
                                }}
                            >
                                <span
                                    style={{
                                        fontSize: 22,
                                        fontWeight: 900,
                                        lineHeight: 1,
                                        color:
                                            idx === 0
                                                ? '#F59E0B'
                                                : idx === 1
                                                  ? '#94A3B8'
                                                  : idx === 2
                                                    ? '#CD7F32'
                                                    : '#cbd5e1',
                                        fontVariantNumeric: 'tabular-nums',
                                    }}
                                >
                                    {c.rank || idx + 1}
                                </span>
                            </div>
                            {/* 썸네일 */}
                            {showThumbs && (
                                <div style={{ width: 68, flexShrink: 0, paddingRight: 12 }}>
                                    {c.imageUrl ? (
                                        <Popover
                                            content={
                                                <img
                                                    src={c.imageUrl}
                                                    alt=""
                                                    style={{
                                                        width: 360,
                                                        height: 360,
                                                        objectFit: 'cover',
                                                        borderRadius: 8,
                                                    }}
                                                />
                                            }
                                            placement="right"
                                            trigger="hover"
                                            styles={{ body: { padding: 6 } }}
                                        >
                                            <img
                                                src={c.imageUrl}
                                                alt=""
                                                style={{
                                                    width: 56,
                                                    height: 56,
                                                    borderRadius: 6,
                                                    objectFit: 'cover',
                                                    cursor: 'zoom-in',
                                                }}
                                            />
                                        </Popover>
                                    ) : (
                                        <div
                                            style={{
                                                width: 56,
                                                height: 56,
                                                background: '#f1f5f9',
                                                borderRadius: 6,
                                            }}
                                        ></div>
                                    )}
                                </div>
                            )}
                            {/* 상품 정보 */}
                            <div style={{ flex: 1, minWidth: 0 }}>
                                <div
                                    style={{
                                        fontSize: 14,
                                        fontWeight: 700,
                                        lineHeight: 1.4,
                                        color: '#0f172a',
                                        marginBottom: 5,
                                    }}
                                >
                                    {c.name}
                                </div>
                                <div
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 10,
                                        marginBottom: 6,
                                    }}
                                >
                                    <span
                                        style={{ fontSize: 13, fontWeight: 700, color: '#f59e0b' }}
                                    >
                                        {c.price ? `${c.price.toLocaleString()}원` : '-'}
                                    </span>
                                    {c.mallUrl &&
                                        (isSample ? (
                                            <span
                                                style={{
                                                    fontSize: 11,
                                                    color: '#94a3b8',
                                                    wordBreak: 'break-all',
                                                }}
                                            >
                                                {c.mallUrl}
                                            </span>
                                        ) : (
                                            <a
                                                href={c.mallUrl}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                referrerPolicy="no-referrer"
                                                style={{ fontSize: 11, color: '#6366f1' }}
                                            >
                                                {c.mallUrl}
                                            </a>
                                        ))}
                                </div>
                                <div
                                    style={{
                                        display: 'flex',
                                        flexWrap: 'wrap',
                                        gap: 4,
                                        minHeight: 22,
                                    }}
                                >
                                    {tagsLoading && !tags.length ? (
                                        <span
                                            style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 5,
                                                fontSize: 11,
                                                color: '#94a3b8',
                                            }}
                                        >
                                            데이터 수집 중...
                                            <Spin size="small" />
                                        </span>
                                    ) : (
                                        tags.map(tag => (
                                            <Tag
                                                key={tag}
                                                style={{
                                                    fontSize: 10,
                                                    borderRadius: 10,
                                                    border: 'none',
                                                    padding: '0 8px',
                                                    margin: 0,
                                                    background: myTags.has(tag)
                                                        ? '#ede9fe'
                                                        : '#fee2e2',
                                                    color: myTags.has(tag) ? '#4f46e5' : '#ef4444',
                                                }}
                                            >
                                                #{tag}
                                            </Tag>
                                        ))
                                    )}
                                </div>
                            </div>
                            <div
                                style={{
                                    width: 88,
                                    textAlign: 'right',
                                    fontSize: 13,
                                    color: '#64748b',
                                    flexShrink: 0,
                                    paddingTop: 8,
                                }}
                            >
                                {c.reviewCount != null ? c.reviewCount.toLocaleString() : '-'}
                            </div>
                            <div
                                style={{
                                    width: 88,
                                    textAlign: 'right',
                                    fontSize: 13,
                                    color: '#64748b',
                                    flexShrink: 0,
                                    paddingTop: 8,
                                }}
                            >
                                {c.wishCount != null ? c.wishCount.toLocaleString() : '-'}
                            </div>
                            <div
                                style={{
                                    width: 88,
                                    textAlign: 'right',
                                    fontSize: 13,
                                    color: '#64748b',
                                    flexShrink: 0,
                                    paddingTop: 8,
                                }}
                            >
                                {tagsLoading && todayVisit == null ? (
                                    <span
                                        style={{
                                            display: 'inline-flex',
                                            alignItems: 'center',
                                            gap: 4,
                                            fontSize: 11,
                                            color: '#94a3b8',
                                        }}
                                    >
                                        수집 중<Spin size="small" />
                                    </span>
                                ) : todayVisit != null ? (
                                    todayVisit.toLocaleString()
                                ) : (
                                    '-'
                                )}
                            </div>
                            <div
                                style={{
                                    width: 88,
                                    textAlign: 'right',
                                    fontSize: 13,
                                    color: '#64748b',
                                    flexShrink: 0,
                                    paddingTop: 8,
                                }}
                            >
                                {tagsLoading && totalVisit == null ? (
                                    <span
                                        style={{
                                            display: 'inline-flex',
                                            alignItems: 'center',
                                            gap: 4,
                                            fontSize: 11,
                                            color: '#94a3b8',
                                        }}
                                    >
                                        수집 중<Spin size="small" />
                                    </span>
                                ) : totalVisit != null ? (
                                    totalVisit.toLocaleString()
                                ) : (
                                    '-'
                                )}
                            </div>
                        </div>
                    );
                })}
            </div>

            {missingFields.length > 0 && (
                <div
                    style={{
                        marginTop: 14,
                        padding: '8px 14px',
                        background: '#fffbeb',
                        borderRadius: 8,
                        fontSize: 12,
                        color: '#92400e',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 6,
                    }}
                >
                    <WarningOutlined style={{ color: '#f59e0b', flexShrink: 0 }} />
                    일부 경쟁사의 <b>{missingFields.join(', ')}</b> 데이터를 가져오지 못했습니다.
                    (해당 항목은 &apos;-&apos;로 표시)
                </div>
            )}
        </Card>
    );
};

export default CompetitorRankingCard;
